import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchBlockedIds, blockUser as blockUserRemote, unblockUser as unblockUserRemote } from '@/services/blocks';

/**
 * Blocked-user store. The blocked IDs are loaded ONCE per session and kept in a
 * Set so every feed / comment filter is a local lookup (no per-post query).
 */
export function useBlockStore(currentUserId: string) {
  const [blockedIds, setBlockedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const loadedFor = useRef<string | null>(null);

  const reload = useCallback(async (userId: string) => {
    setLoading(true);
    const ids = await fetchBlockedIds(userId);
    setBlockedIds(new Set(ids));
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!currentUserId || currentUserId === '__none__') {
      setBlockedIds(new Set());
      setLoading(false);
      return;
    }
    if (loadedFor.current === currentUserId) return;
    loadedFor.current = currentUserId;
    void reload(currentUserId);
  }, [currentUserId, reload]);

  const isBlocked = useCallback((userId: string) => blockedIds.has(userId), [blockedIds]);

  const blockUser = useCallback(async (userId: string): Promise<{ error?: string }> => {
    if (!currentUserId || currentUserId === '__none__' || userId === currentUserId) return {};
    setBlockedIds((prev) => new Set(prev).add(userId));
    const { error } = await blockUserRemote(currentUserId, userId);
    if (error) {
      setBlockedIds((prev) => {
        const next = new Set(prev);
        next.delete(userId);
        return next;
      });
    }
    return { error };
  }, [currentUserId]);

  const unblockUser = useCallback(async (userId: string): Promise<{ error?: string }> => {
    if (!currentUserId || currentUserId === '__none__') return {};
    setBlockedIds((prev) => {
      const next = new Set(prev);
      next.delete(userId);
      return next;
    });
    const { error } = await unblockUserRemote(currentUserId, userId);
    if (error) setBlockedIds((prev) => new Set(prev).add(userId));
    return { error };
  }, [currentUserId]);

  return { blockedIds, loading, isBlocked, blockUser, unblockUser, reload };
}
